import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Wrench, Check, FileText, Users, Image as ImageIcon, Type } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { getActiveMetaPages } from '@/services/api';
import { getUtilityTemplateGroupsAll } from '@/services/api/broadcast';
import type { Page } from '@/types/broadcast';
import type { BroadcastType } from '../types/broadcast.types';

interface StepSelectPageProps {
    selectedPages: Page[];
    onTogglePage: (page: Page) => void;
    onSetPages: (pages: Page[]) => void;
    broadcastType?: BroadcastType;
    utilityTemplateId?: number | null;
    onBroadcastTypeChange?: (type: BroadcastType) => void;
    onUtilityTemplateChange?: (
        id: number | null,
        data?: {
            id: number;
            template_name: string;
            title?: string;
            subtitle?: string;
            image_url?: string | null;
            thumbnail_url?: string | null;
            approved_count?: number;
            buttons?: Array<{ type: string; text: string; url_base?: string }>;
        } | null
    ) => void;
    /** Template complementar opcional — enviado em sequência após o principal pro mesmo lead. */
    secondaryUtilityTemplateId?: number | null;
    onSecondaryUtilityTemplateChange?: (
        id: number | null,
        data?: { id: number; template_name: string; title?: string } | null
    ) => void;
}

export default function StepSelectPage({
    selectedPages,
    onTogglePage,
    onSetPages,
    broadcastType = 'standard',
    utilityTemplateId,
    onUtilityTemplateChange,
    secondaryUtilityTemplateId,
    onSecondaryUtilityTemplateChange,
}: StepSelectPageProps) {
    const [showSecondaryPicker, setShowSecondaryPicker] = useState(false);
    const [activeCategoryIds, setActiveCategoryIds] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const { data: activePagesData = [] as Page[], isLoading: loadingPages, error: pagesErrorObj, refetch: refetchPages } = useQuery({
        queryKey: ['active-pages'],
        queryFn: getActiveMetaPages,
        staleTime: 1000 * 60 * 5, // 5 minutos de cache
    });

    const pages = useMemo(() => {
        const seen = new Set<string>();
        return activePagesData.filter((p: Page) => {
            if (seen.has(p.page_id)) return false;
            seen.add(p.page_id);
            return true;
        });
    }, [activePagesData]);

    const { data: allUtilityGroups = [], isLoading: loadingGroups } = useQuery({
        queryKey: ['utility-groups'],
        queryFn: getUtilityTemplateGroupsAll,
        enabled: broadcastType === 'utility',
        staleTime: 1000 * 60 * 5, // 5 minutos de cache
    });

    // Biblioteca é global (todos os clientes), mas aqui na seleção do
    // broadcast só faz sentido escolher os próprios templates — e só os que
    // já têm ao menos 1 página aprovada (senão o broadcast não tem pra quem enviar).
    const utilityGroups = useMemo(
        () => allUtilityGroups.filter(g => g.is_mine === true && (g.approved_count ?? 0) > 0),
        [allUtilityGroups]
    );

    const loading = loadingPages || (broadcastType === 'utility' && loadingGroups);
    const error = pagesErrorObj instanceof Error ? pagesErrorObj.message : null;

    // Drop ghost pages: entries pre-filled (e.g. via duplicate) that no longer
    // exist in the user's accessible pages would otherwise stay invisible in
    // selectedPages and reach the backend as invalid IDs.
    useEffect(() => {
        if (pages.length === 0) return;
        const validIds = new Set(pages.map(p => p.page_id));
        if (selectedPages.some(p => !validIds.has(p.page_id))) {
            onSetPages(selectedPages.filter(p => validIds.has(p.page_id)));
        }
    }, [pages, selectedPages, onSetPages]);

    const normalize = (str: string) =>
        str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    const categories = Array.from(new Map(
        pages
            .filter(p => p.internal_category_id && p.internal_category_name)
            .map(p => [p.internal_category_id, p.internal_category_name])
    ).entries()).map(([id, name]) => ({ id, name }));

    const filteredPages = pages.filter(page => {
        const matchesCategory =
            activeCategoryIds.length === 0 ||
            (page.internal_category_id != null && activeCategoryIds.includes(String(page.internal_category_id)));
        if (!matchesCategory) return false;

        if (!searchTerm) return true;
        const term = normalize(searchTerm);
        return (
            normalize(page.page_name).includes(term) ||
            normalize(page.page_id).includes(term)
        );
    });

    const isSelected = (pageId: string) => selectedPages.some(p => p.page_id === pageId);

    const handleCategoryChange = (vals: string[]) => {
        const added = vals.filter(id => !activeCategoryIds.includes(id));
        const removed = activeCategoryIds.filter(id => !vals.includes(id));

        let updatedPages = [...selectedPages];

        // Remove páginas das categorias desmarcadas
        if (removed.length > 0) {
            const toRemove = new Set(
                pages
                    .filter(p => p.internal_category_id != null && removed.includes(String(p.internal_category_id)))
                    .map(p => p.page_id)
            );
            updatedPages = updatedPages.filter(p => !toRemove.has(p.page_id));
        }

        // Adiciona páginas das categorias marcadas
        if (added.length > 0) {
            const current = new Set(updatedPages.map(p => p.page_id));
            const toAdd = pages.filter(p =>
                p.internal_category_id != null &&
                added.includes(String(p.internal_category_id)) &&
                !current.has(p.page_id)
            );
            updatedPages = [...updatedPages, ...toAdd];
        }

        setActiveCategoryIds(vals);
        onSetPages(updatedPages);
    };

    const someFilteredSelected = filteredPages.some(p => isSelected(p.page_id));

    const handleToggleAll = () => {
        if (someFilteredSelected) {
            // Desmarca só as que estão visíveis/filtradas
            const filteredIds = new Set(filteredPages.map(p => p.page_id));
            onSetPages(selectedPages.filter(p => !filteredIds.has(p.page_id)));
        } else {
            const currentIds = new Set(selectedPages.map(p => p.page_id));
            const toAdd = filteredPages.filter(p => !currentIds.has(p.page_id));
            onSetPages([...selectedPages, ...toAdd]);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#7dd3fc] mb-4" />
                <p className="text-gray-600">Carregando suas páginas...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Erro ao Carregar Páginas</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <button onClick={() => refetchPages()} className="px-4 py-2 bg-[#0f172a] text-white rounded-lg hover:bg-[#1e293b] transition-colors">
                    Tentar Novamente
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* UTILITY: seleção de grupo — substitui seleção manual de páginas */}
            {broadcastType === 'utility' ? (
                <div className="space-y-4">
                    <div className="text-center mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">Selecione o Template</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            O sistema envia automaticamente para todas as páginas aprovadas do template
                        </p>
                    </div>

                    {loadingGroups ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-[#7dd3fc] mr-2" />
                            <span className="text-gray-500 text-sm">Carregando templates...</span>
                        </div>
                    ) : utilityGroups.length === 0 ? (
                        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                            <Wrench className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 font-medium">Nenhum template utility encontrado</p>
                            <p className="text-xs text-gray-400 mt-1">Crie e aprove um template em Utility Templates</p>
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                            {utilityGroups.map(group => {
                                const isSelected = utilityTemplateId === group.id;
                                // Conta aprovados: usa campo do backend ou assume todos
                                const approvedCount = group.approved_count ?? '?';
                                return (
                                    <button
                                        key={group.id}
                                        type="button"
                                        onClick={() => {
                                            onUtilityTemplateChange?.(
                                                group.id,
                                                {
                                                    id: group.id,
                                                    template_name: group.template_name,
                                                    title: group.title,
                                                    subtitle: group.subtitle,
                                                    image_url: group.image_url ?? null,
                                                    thumbnail_url: group.thumbnail_url ?? null,
                                                    approved_count: group.approved_count,
                                                    buttons: group.buttons,
                                                }
                                            );
                                        }}
                                        className={`relative w-full p-4 border-2 rounded-xl text-left transition-all hover:border-[#7dd3fc] hover:shadow-md ${
                                            isSelected ? 'border-[#7dd3fc] bg-[#7dd3fc]/5 shadow-md' : 'border-gray-200 bg-white'
                                        }`}
                                    >
                                        <div className="flex items-center gap-4">
                                            {/* Thumbnail (com imagem) ou selo "só texto" */}
                                            <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                                                {group.thumbnail_url || group.image_url ? (
                                                    <img
                                                        src={group.thumbnail_url || group.image_url!}
                                                        alt={group.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center gap-0.5 bg-blue-50">
                                                        <Type className="w-5 h-5 text-blue-400" />
                                                        <span className="text-[8px] font-semibold text-blue-500 uppercase tracking-wide">Texto</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-600 rounded-md shrink-0">
                                                        #{group.id}
                                                    </span>
                                                    <h4 className="font-semibold text-gray-900 truncate">{group.template_name}</h4>
                                                    <span
                                                        className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium ${
                                                            group.thumbnail_url || group.image_url
                                                                ? 'bg-indigo-50 text-indigo-600'
                                                                : 'bg-blue-50 text-blue-600'
                                                        }`}
                                                    >
                                                        {group.thumbnail_url || group.image_url ? (
                                                            <><ImageIcon className="w-2.5 h-2.5" /> Imagem</>
                                                        ) : (
                                                            <><Type className="w-2.5 h-2.5" /> Só texto</>
                                                        )}
                                                    </span>
                                                    {isSelected && (
                                                        <div className="flex-shrink-0 w-5 h-5 bg-[#7dd3fc] rounded-full flex items-center justify-center">
                                                            <Check className="w-3 h-3 text-white" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1.5 mt-1.5">
                                                    <Users className="w-3 h-3 text-emerald-500" />
                                                    <span className="text-xs font-medium text-emerald-600">
                                                        {approvedCount} página{approvedCount !== 1 ? 's' : ''} aprovada{approvedCount !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {utilityTemplateId && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
                            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            <p className="text-sm text-emerald-700">
                                Template selecionado. O broadcast será enviado automaticamente para todas as páginas aprovadas.
                            </p>
                        </div>
                    )}

                    {/* Template complementar opcional — enviado em sequência
                        logo após o principal, pro mesmo lead. Independente
                        do principal (sem associação de grupo no banco). */}
                    {utilityTemplateId && (
                        <div className="pt-2">
                            {!showSecondaryPicker && !secondaryUtilityTemplateId ? (
                                <button
                                    type="button"
                                    onClick={() => setShowSecondaryPicker(true)}
                                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-[#7dd3fc] hover:text-[#0f172a] hover:bg-[#7dd3fc]/5 transition-colors"
                                >
                                    <FileText className="w-4 h-4" />
                                    Adicionar template complementar (opcional)
                                </button>
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-sm font-medium">Template complementar (opcional)</Label>
                                        {secondaryUtilityTemplateId && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onSecondaryUtilityTemplateChange?.(null, null);
                                                    setShowSecondaryPicker(false);
                                                }}
                                                className="text-xs text-gray-400 hover:text-red-500"
                                            >
                                                Remover
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Enviado logo após o principal, para o mesmo lead. Páginas sem esse template
                                        aprovado ainda recebem o principal normalmente.
                                    </p>
                                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                        {utilityGroups
                                            .filter((g) => g.id !== utilityTemplateId)
                                            .map((group) => {
                                                const isSecondarySelected = secondaryUtilityTemplateId === group.id;
                                                return (
                                                    <button
                                                        key={group.id}
                                                        type="button"
                                                        onClick={() =>
                                                            onSecondaryUtilityTemplateChange?.(
                                                                isSecondarySelected ? null : group.id,
                                                                isSecondarySelected
                                                                    ? null
                                                                    : {
                                                                          id: group.id,
                                                                          template_name: group.template_name,
                                                                          title: group.title,
                                                                      }
                                                            )
                                                        }
                                                        className={`relative w-full p-3 border-2 rounded-lg text-left transition-all hover:border-[#7dd3fc] ${
                                                            isSecondarySelected
                                                                ? 'border-[#7dd3fc] bg-[#7dd3fc]/5'
                                                                : 'border-gray-200 bg-white'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                                                                {group.thumbnail_url || group.image_url ? (
                                                                    <img
                                                                        src={group.thumbnail_url || group.image_url!}
                                                                        alt={group.title}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center bg-blue-50">
                                                                        <Type className="w-4 h-4 text-blue-400" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <span
                                                                    className={`inline-flex items-center gap-1 mb-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-medium ${
                                                                        group.thumbnail_url || group.image_url
                                                                            ? 'bg-indigo-50 text-indigo-600'
                                                                            : 'bg-blue-50 text-blue-600'
                                                                    }`}
                                                                >
                                                                    {group.thumbnail_url || group.image_url ? (
                                                                        <><ImageIcon className="w-2.5 h-2.5" /> Imagem</>
                                                                    ) : (
                                                                        <><Type className="w-2.5 h-2.5" /> Só texto</>
                                                                    )}
                                                                </span>
                                                                <span className="font-medium text-gray-900 text-sm truncate block">
                                                                    {group.template_name}
                                                                </span>
                                                                <span className="text-xs text-gray-400">
                                                                    {group.approved_count ?? '?'} página(s) aprovada(s)
                                                                </span>
                                                            </div>
                                                            {isSecondarySelected && (
                                                                <Check className="w-4 h-4 text-[#7dd3fc] shrink-0" />
                                                            )}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                /* STANDARD/MESSAGE_TAG: seleção normal de páginas */
                <>
                    <div className="text-center mb-6">
                        <h3 className="text-xl font-semibold text-gray-900">Selecione as Páginas</h3>
                        <p className="text-sm text-gray-600 mt-2">Escolha quais páginas do Facebook enviarão o broadcast</p>
                        {selectedPages.length > 0 && (
                            <div className="mt-3 inline-flex items-center gap-2 bg-[#7dd3fc]/10 text-[#0f172a] px-3 py-1.5 rounded-full text-sm font-medium">
                                <Check className="h-4 w-4" />
                                {selectedPages.length} página{selectedPages.length > 1 ? 's' : ''} selecionada{selectedPages.length > 1 ? 's' : ''}
                            </div>
                        )}
                    </div>

                    {/* Search + Category + Select All */}
                    <div className="flex flex-col md:flex-row items-end gap-3">
                        <div className="flex-1 w-full space-y-2">
                            <Label htmlFor="broadcast-page-search" className="text-sm font-medium">Buscar Página</Label>
                            <Input
                                id="broadcast-page-search"
                                type="text"
                                autoComplete="off"
                                placeholder="Busque por nome ou ID..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full"
                            />
                        </div>
                        <div className="w-full md:w-64 space-y-2">
                            <Label className="text-sm font-medium">Filtrar por Categoria</Label>
                            <SearchableMultiSelect
                                options={categories.map(c => ({ value: String(c.id), label: c.name || 'Sem nome' }))}
                                values={activeCategoryIds}
                                onValuesChange={handleCategoryChange}
                                placeholder="Todas as categorias"
                                searchPlaceholder="Buscar categoria..."
                                showTags={false}
                                selectedLabel={n => `${n} categoria${n > 1 ? 's' : ''} selecionada${n > 1 ? 's' : ''}`}
                            />
                        </div>
                        <Button variant="outline" size="sm" onClick={handleToggleAll} className="whitespace-nowrap h-9">
                            {someFilteredSelected ? 'Desmarcar Todas' : 'Selecionar Todas'}
                        </Button>
                    </div>

                    {/* Lista de Páginas */}
                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                        {filteredPages.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-gray-500">Nenhuma página encontrada</p>
                            </div>
                        ) : (
                            filteredPages.map(page => {
                                const selected = isSelected(page.page_id);
                                return (
                                    <button
                                        key={page.page_id}
                                        onClick={() => onTogglePage(page)}
                                        className={`relative w-full p-4 border-2 rounded-xl text-left transition-all hover:border-[#7dd3fc] hover:shadow-md
                                            ${selected ? 'border-[#7dd3fc] bg-[#7dd3fc]/5 shadow-md' : 'border-gray-200 bg-white'}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors
                                                ${selected ? 'bg-[#7dd3fc] border-[#7dd3fc]' : 'border-gray-300 bg-white'}`}>
                                                {selected && <Check className="h-4 w-4 text-white" />}
                                            </div>
                                            <div className="relative flex-shrink-0">
                                                <div className="w-14 h-14 bg-[#0f172a] rounded-full flex items-center justify-center">
                                                    <span className="text-white font-semibold text-base">
                                                        {page.page_name.substring(0, 2).toUpperCase()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <h4 className="font-semibold text-gray-900 truncate">{page.page_name}</h4>
                                                    {page.internal_category_name && (
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 uppercase tracking-wider">
                                                            {page.internal_category_name}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-500 truncate">ID: {page.page_id}</p>
                                                {page.meta_user_name && (
                                                    <p className="text-xs text-gray-400 truncate">Conta: {page.meta_user_name}</p>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-900">
                            <strong>Dica:</strong> Será criado 1 broadcast por página selecionada.
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}
